/** Кривая капитала по снимкам.
 *
 * Точки — только реальные снимки, соединённые прямыми. Плавную кривую здесь
 * рисовать нельзя: капитал между снимками неизвестен, и сглаживание показывало
 * бы данные, которых нет.
 *
 * Цвет — `--data`, а не синий: линию нельзя нажать, а синим у нас красится
 * только интерактивное.
 */
import type { CapitalPoint } from "../lib/api.ts";

const W = 320;
const H = 96;
const PAD = 10;   // радиус точки 3 плюс запас: крайняя точка не должна срезаться

export function Sparkline({ points }: { points: CapitalPoint[] }) {
  if (points.length < 2) return null;

  const values = points.map((p) => Number(p.total));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = (W - PAD * 2) / (points.length - 1);

  const xy = values.map((v, i) => [
    PAD + i * step,
    PAD + (1 - (v - min) / span) * (H - PAD * 2),
  ] as const);
  const line = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${PAD},${H} ${line} ${W - PAD},${H}`;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ marginTop: "var(--s-4)" }}
         role="img" aria-label={`Капитал по ${points.length} снимкам`}>
      <defs>
        <linearGradient id="cap" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--data)" stopOpacity="0.22" />
          <stop offset="1" stopColor="var(--data)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#cap)" />
      <polyline points={line} fill="none" stroke="var(--data)" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round" />
      {xy.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3" fill="var(--data)" />
      ))}
    </svg>
  );
}
